import { usePublicLanguage } from "../context/PublicLanguage";
import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import StoryCard from "@/components/StoryCard";
import Newsletter from "@/components/public/Newsletter";
import AdSlot from "@/components/public/AdSlot";
import { categoryLabel } from "@/components/CategoryTag";

const DESCRIPTIONS = {
  ai: "Foundation models, agents, chips, research, regulation and the business of artificial intelligence.",
  robotics: "Humanoids, industrial automation, embodied AI and the machines learning to move.",
  cybersecurity: "Threat intelligence, vulnerabilities, defense, privacy and the security of critical systems.",
  "future-tech": "Quantum, energy, space, biotech and the frontier technologies shaping the next decade.",
  business: "Funding, markets, strategy and the economics behind the technology industry.",
  guides: "Clear, careful explainers that turn complex technology into something you can actually use.",
};

export default function CategoryPage() {
  const { t, language } = usePublicLanguage();
  const { category } = useParams();
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["cat", category],
    queryFn: () => publicApi.articles({ category, limit: 40 }),
  });
  const label = categoryLabel(category, language);

  useSEO({
    title: `${t(label)} — TechDishes`,
    description: t(DESCRIPTIONS[category]) || `${t(label)} coverage on TechDishes.`,
    url: window.location.href,
  });

  const [lead, ...rest] = articles;

  return (
    <div className="mx-auto max-w-[1320px] px-5 md:px-8 py-10 md:py-14">
      <header className="mb-7 border-b pb-5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="td-eyebrow mb-3" style={{ color: "#FF8A1F" }}>{t('Section')}</div>
        <h1 className="font-display font-extrabold tracking-tight text-white text-4xl md:text-5xl lg:text-6xl" style={{ letterSpacing: "-0.03em" }}>
          {t(label)}
        </h1>
        <p className="mt-4 text-[#A7ABB0] max-w-2xl leading-relaxed" style={{ fontSize: "1.1rem" }}>
          {t(DESCRIPTIONS[category])}
        </p>
      </header>

      {isLoading ? (
        <p className="text-[#A7ABB0]">{t('Loading…')}</p>
      ) : articles.length === 0 ? (
        <p className="text-[#A7ABB0]">{t('No stories in this section yet.')}</p>
      ) : (
        <>
          {lead && (
            <section className="mb-7">
              <StoryCard article={lead} size="lg" featured />
            </section>
          )}
          <AdSlot height={110} className="mb-12" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((a) => (
              <StoryCard key={a.slug} article={a} />
            ))}
          </div>
        </>
      )}

      <Newsletter />
    </div>
  );
}
